import { FC, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { currentStyleSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { AppRoute } from '@chegg-wt/core/types/core'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import {
  CARD_IMAGE_CAROUSEL_TEST_ID,
  CitationFunnelVariations,
  IMAGE_CAROUSEL_TEST_ID,
  homepageCtaMapCardImage,
} from '@chegg-wt/core-wa/constants/paraphraser'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { HOMEPAGE_CAROUSEL_AUTOSCROLL_DELAY_MS } from '@chegg-wt/core-wa/state/constants'
import { citationFunnelVariationSelector } from '@chegg-wt/core-wa/state/selectors'
import { Config } from '@chegg-wt/core/config'
import { EB_PRODUCT_NAME, CM_PRODUCT_NAME } from '@chegg-wt/core/constants/site'
import { CarouselCard } from './CarouselCard'
import { CardImageCarouselContainer, CarouselCardsContainer } from './styled'
import { ImageCarousel } from './ImageCarousel'
import { getCarouselCardsWithImage } from './utils'

const CardImageCarousel: FC = () => {
  const [currentPosition, setCurrentPosition] = useState(0)
  const { toPlagiarism, toSourceTypeSelection, toWritingAssistant } = useRoutesContext()

  const currentStyle = useSelector(currentStyleSelector)
  const citationFunnelVariation = useSelector(citationFunnelVariationSelector)
  const isEitherCitationFunnelVariations =
    [CitationFunnelVariations.VARIANT1, CitationFunnelVariations.VARIANT2].includes(
      citationFunnelVariation
    ) || Config.siteName === EB_PRODUCT_NAME || Config.siteName === CM_PRODUCT_NAME
  const isCitationFunnelVariant2 =
    citationFunnelVariation === CitationFunnelVariations.VARIANT2

  const dispatch = useDispatch()

  const carouselCardsWithImage = getCarouselCardsWithImage({
    writingAssistantRoute: toWritingAssistant(),
    plagiarismRoute: toPlagiarism(),
    createCitationRoute: toSourceTypeSelection({ style: currentStyle }),
    isCitationFunnelVariant2,
  })
  const currentCarouselCard = carouselCardsWithImage[currentPosition]

  const handleCardClick = (index: number, dataTestId: string, redirect: AppRoute) => {
    const isActiveCard = index === currentPosition

    if (isActiveCard || isEitherCitationFunnelVariations) dispatch(navigated(redirect))

    setCurrentPosition(index)

    dispatch(
      userClick({
        origin: origins.carousalImageGetStartedClick,
        name: homepageCtaMapCardImage[dataTestId].name,
        region: homepageCtaMapCardImage[dataTestId].region,
      })
    )
  }
  const handleLeftArrowClick = () => {
    if (currentPosition > 0) setCurrentPosition(currentPosition - 1)
    else setCurrentPosition(carouselCardsWithImage.length - 1)
  }
  const handleRightArrowClick = useCallback(() => {
    if (currentPosition < carouselCardsWithImage.length - 1)
      setCurrentPosition(currentPosition + 1)
    else setCurrentPosition(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition])

  const handleCarouselImageClick = () =>
    dispatch(navigated(currentCarouselCard.actionButtonRoute))

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>
    if (isEitherCitationFunnelVariations) {
      intervalId = setInterval(() => {
        handleRightArrowClick()
      }, HOMEPAGE_CAROUSEL_AUTOSCROLL_DELAY_MS)
    }

    return () => {
      clearInterval(intervalId)
    }
  }, [isEitherCitationFunnelVariations, handleRightArrowClick])

  return (
    <CardImageCarouselContainer data-test={CARD_IMAGE_CAROUSEL_TEST_ID}>
      <CarouselCardsContainer>
        {carouselCardsWithImage.map((cardData, index) => (
          <CarouselCard
            {...cardData}
            key={cardData.dataTestId}
            isActive={currentPosition === index}
            onClick={() =>
              handleCardClick(index, cardData.dataTestId, cardData.actionButtonRoute)
            }
          />
        ))}
      </CarouselCardsContainer>

      <ImageCarousel
        currentCarouselCard={currentCarouselCard}
        onLeftArrowClick={handleLeftArrowClick}
        onRightArrowClick={handleRightArrowClick}
        imageClickEnabled={isEitherCitationFunnelVariations}
        onImageClick={handleCarouselImageClick}
        dataTestId={IMAGE_CAROUSEL_TEST_ID}
      />
    </CardImageCarouselContainer>
  )
}

export default CardImageCarousel
